import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { StateStoreService } from '../services/state-store/state-store';

/**
 * Frameworks route gate — `framework_read` on the **effective** org.
 *
 * Replaces `complianceGuard` (list) and `managerGuard` (detail), both of which
 * answered on org type and so blocked every STANDARD org outright. That
 * premise is retired: `_CUSTOMER_ORG_CEILING` in crp-be carries
 * `framework_write`, `can_act_on_compliance` permits a STANDARD caller on its
 * own home org, and VER-326 already wrote STANDARD handling into the page's
 * `createWriteActionGate` — branches the old guards made unreachable.
 *
 * Effective rather than home, matching what the page fetches: a manager
 * browsing a picked customer is asking about that customer's frameworks.
 *
 * **Reads only.** Creating, editing and archiving stay behind the page's
 * `actionGate`, which folds in `framework_write`, the subscription, the
 * `frameworks` entitlement and its read/write mode — each with its own
 * tooltip naming the actual blocker.
 *
 * **Entitlement is deliberately not checked here.** An org whose plan lacks
 * Frameworks still reaches the page and meets the upsell; hiding a feature
 * someone could buy is how you lose the sale. Trust Centre is the one
 * documented exception in this app (see `SidebarComponent.canAccessTrustCenter`
 * for why), and it is an exception, not the pattern.
 *
 * Permissive when the backend composed no access block — `hasCapability`
 * returns true then, so this only ever blocks someone explicitly narrowed.
 * Readiness is guaranteed by `appReadyMatch` at the route level.
 */
export const frameworksGuard: CanActivateFn = () => {
  const state = inject(StateStoreService);
  const router = inject(Router);

  return state.hasCapability('framework_read') ? true : router.createUrlTree(['/assessment']);
};
